import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ButtonGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FlipCard } from '../../components/FlipCard'
import ProtectedButton from '../../components/ProtectedButton'
import { SelectDialogProps } from '../../components/SelectDialog'
import Loading from '../../layout/Loading'
import TokenProfileService from '../../service/TokenProfileService'
import { expandRowFunction, formatBooleanColumn } from '../../utils/DatatableUtil'
import { getDataField } from '../../utils/DialogUtil'
import GatewayType from '../GatewayType'

const TokenProfiles: GatewayType = () => {
  const { t } = useTranslation()
  const [tokenProfiles, setTokenProfiles] = useState([] as any[])
  const [tokenProfileDefinition, setTokenProfileDefinition] = useState([] as any[])
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      await TokenProfileService.getConfigurableTokenProfiles(setTokenProfiles)
      await TokenProfileService.getDefinitions(setTokenProfileDefinition)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('name.text'),
        dataField: 'name',
        sort: true,
      },
      {
        text: t('type.text'),
        dataField: 'type',
        sort: true,
      },
      {
        text: t('tokenProfile.totallength'),
        dataField: 'totallength',
        sort: true,
      },
      {
        text: t('tokenProfile.generic'),
        dataField: 'generic',
        align: 'center',
        formatter: (cell: any) => formatBooleanColumn(cell),
        sort: true,
      },
      {
        hidden:
          !AuthService.hasPermission(userPermissions, Authorization.tokenprofiles.delete) || tokenProfiles.find((profile) => profile.deletable) === undefined,
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <ProtectedButton
              permissions={Authorization.tokenprofiles.delete}
              shouldRender={row.deletable}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteTokenProfileDialog(t, confirmDialogPropsSetter, setTokenProfiles, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter, tokenProfiles])

  const expandRow = expandRowFunction(renderer, () => {}, tokenProfileDefinition, t)

  const defaultSorted = [
    {
      dataField: 'type',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={tokenProfiles.sort((a, b) => a.name.localeCompare(b.name))}
        columns={columns}
        defaultSorted={defaultSorted}
        caption={t('tokenProfile.text')}
        helpLink={t('tokenProfile.helpLink')}
        hover={false}
        expandRow={expandRow}
        searchPlaceholder='Search Token Profiles'
        captionButtons={[
          {
            onClick: () => createTokenProfileDialog(t, editDialogPropsSetter, setTokenProfiles, tokenProfileDefinition),
            text: t('new'),
            permissions: Authorization.tokenprofiles.create,
          },
        ]}
      />
    </Loading>
  )
}

function renderer(row: any, rowIndex: any, ...args: any[]) {
  const tokenProfileDefinition: any[] = args[0]
  const t: TFunction = args[1]
  return tokenProfileDefinition
    .filter((definition) => definition.type === row.type)
    .map((definition: { attributes: any[] }) => {
      const columns: any[] = [
        {
          text: t('attribute'),
          dataField: 'attribute',
          classes: 'col-sm-2',
          sort: true,
        },
        {
          text: t('value'),
          dataField: 'value',
          classes: 'col-sm-10',
          formatter: (cell: any, row: any) => {
            if (row.type === 'BOOLEAN') {
              return formatBooleanColumn(cell)
            }
            return cell
          },
          sort: true,
        },
      ]
      const data: any[] = definition.attributes
        .filter((attribute: any) => attribute.origin === 'SUBCLASS')
        .map((attribute: any) => ({
          attribute: t(attribute.label),
          value: row[attribute.name],
          type: attribute.type,
        }))

      data.push(
        {
          attribute: t('tokenProfile.possibletokens'),
          value: row['possibletokens'],
          type: 'STRING',
        },
        {
          attribute: t('tokenProfile.unique'),
          value: row['unique'],
          type: 'BOOLEAN',
        },
        {
          attribute: t('tokenProfile.reversible'),
          value: row['reversible'],
          type: 'BOOLEAN',
        }
      )

      if (!data.length) {
        return <React.Fragment key={row.name} />
      }
      return (
        <React.Fragment key={row.name}>
          <DataTable
            data={data}
            columns={columns}
            searchable={false}
            headerClasses='d-none'
            striped={false}
            hover={false}
            bodyClasses='container tr-row'
            condensed={true}
            noDataIndication={() => <span className='text-center'>{t('tokenProfile.noAttributeFound')}</span>}
          />
        </React.Fragment>
      )
    })
}

function createTokenProfileDialog(t: TFunction, editDialogPropsSetter: any, setTokenProfiles: React.Dispatch<any>, tokenProfileDefinition: any[]) {
  const sortedTokenProfileDefinition = tokenProfileDefinition.sort((a, b) => a.label.localeCompare(b.label))
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('tokenProfile.createHeader'),
    onSubmit: TokenProfileService.createTokenProfile,
    onSubmitExtraArgs: [t, setTokenProfiles],
    canForceUpdate: true,
    data: (custom: string[]) => [
      {
        id: 'type',
        type: 'select',
        options: sortedTokenProfileDefinition.map((definition) => ({ label: definition.label, value: definition.type })),
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
        value: custom[0],
        label: t('type.text'),
        required: true,
      },
      ...getSubClassDefinitions(sortedTokenProfileDefinition, custom),
    ],
    initCustom: [sortedTokenProfileDefinition[0].type],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteTokenProfileDialog(t: TFunction, confirmDialogPropsSetter: any, setTokenProfiles: any, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('tokenProfile.deleteHeader'),
    body: t('tokenProfile.deleteBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: TokenProfileService.deleteTokenProfile,
    onSubmitExtraArgs: [row, t, setTokenProfiles],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

function selectTokenProfileDialog(t: TFunction, selectDialogPropsSetter: any, admins: any[], onSubmit: any, onSubmitExtraArgs?: any[]) {
  const selectDialogProps: SelectDialogProps = {
    show: true,
    header: t('tokenProfile.selectHeader'),
    data: admins,
    keyField: 'name',
    label: t('tokenProfile.selectHeader'),
    submitText: t('select'),
    onSubmit,
    onSubmitExtraArgs,
    cancelText: t('cancel'),
  }
  selectDialogPropsSetter(selectDialogProps)
}

function getSubClassDefinitions(tokenProfileDefinition: any[], custom: string[]) {
  return tokenProfileDefinition
    .map((definition) => {
      definition.attributes.forEach((attribute: any) => (attribute.profile = definition.type))
      return definition.attributes
    })
    .reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
    .filter((attribute: any) => attribute.name !== 'type' && attribute.profile === custom[0])
    .map((attribute: any) => getDataField(attribute, attribute.profile))
}

export const TokenProfileFlipCard = () => {
  const [tokenProfiles, setTokenProfiles] = useState([] as any[])
  const [tokenProfileDefinition, setTokenProfileDefinition] = useState([] as any[])
  const { t } = useTranslation()
  const { userPermissions } = useContext(AppContext)
  const { selectDialogPropsSetter, editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)

  const permission = Authorization.tokenprofiles
  useEffect(() => {
    if (AuthService.hasPermission(userPermissions, permission.view)) {
      TokenProfileService.getConfigurableTokenProfiles(setTokenProfiles)
      TokenProfileService.getDefinitions(setTokenProfileDefinition)
    }
  }, [userPermissions, permission])

  return (
    <FlipCard
      permissions={permission.view}
      headerText={t('tokenProfile.text')}
      headerLink='./tokenProfile'
      flipFrontContent={
        <ButtonGroup vertical>
          <ProtectedButton
            permissions={permission.create}
            variant='link'
            className='m-1'
            onClick={() => createTokenProfileDialog(t, editDialogPropsSetter, setTokenProfiles, tokenProfileDefinition)}>
            {t('tokenProfile.createHeader')}
          </ProtectedButton>

          <ProtectedButton
            permissions={permission.delete}
            variant='link'
            className='m-1'
            onClick={() =>
              selectTokenProfileDialog(
                t,
                selectDialogPropsSetter,
                tokenProfiles.filter((profile) => profile.deletable),
                deleteTokenProfileDialog,
                [t, confirmDialogPropsSetter, setTokenProfiles]
              )
            }>
            {t('tokenProfile.deleteHeader')}
          </ProtectedButton>
        </ButtonGroup>
      }
    />
  )
}

export default TokenProfiles
